import React, { createContext, useContext, useRef, useState } from "react";
import {
  Receipt,
  ReceiptItem,
  ReceiptPaymentMethod,
  newReceipt,
  newReceiptItem,
  newReceiptPaymentMethod,
  RECEIPT_PAYMENT_METHOD_CASH,
} from "../../../models/receipt";
import Establishment from "../../../models/establishment";
import DispatchPoint from "../../../models/dispatch-point";
import Stamp from "../../../models/stamp";
import Client from "../../../models/client";
import { Invoice } from "../../../models/invoice";
import Errors from "../../../models/errors";
import { PYG } from "../../../currency/available-currencies";
import moment from "moment";
import { ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE } from "../../../models/associated-document";
import { changeTZToUtc } from "../../../utils/dates";

interface ReceiptFormContextValue {
  // Refs and State
  receiptRef: React.MutableRefObject<Receipt | null>;
  receiptDate: Date | undefined;
  currency: string;
  paymentMethod: string;
  totalAmount: number | undefined;
  establishment: Establishment | null;
  dispatchPoint: DispatchPoint | null;
  stamp: Stamp | null;
  client: Client | null;
  associatedDocumentType: string | undefined;
  electronicInvoice: Invoice | null;
  items: ReceiptItem[];
  paymentMethods: ReceiptPaymentMethod[];
  errors: Errors;
  readonly?: boolean;

  // Setters
  setReceiptDate: (date: Date | undefined) => void;
  setCurrency: (currency: string) => void;
  setPaymentMethod: (method: string) => void;
  setTotalAmount: (amount: number | undefined) => void;
  setEstablishment: (est: Establishment | null) => void;
  setDispatchPoint: (dp: DispatchPoint | null) => void;
  setStamp: (stamp: Stamp | null) => void;
  setClient: (client: Client | null) => void;
  setAssociatedDocumentType: (type: string | undefined) => void;
  setElectronicInvoice: (invoice: Invoice | null) => void;
  setItems: (items: ReceiptItem[]) => void;
  setPaymentMethods: (payments: ReceiptPaymentMethod[]) => void;
  setErrors: (errors: Errors) => void;

  // Handlers
  onEstablishmentChange: (newEstablishment: Establishment | null) => void;
  onDispatchPointChange: (newDispatchPoint: DispatchPoint | null) => void;
  onStampChange: (newStamp: Stamp | null) => void;
  onClientChange: (newClient: Client | null) => void;
  onReceiptDateChange: (value: string | moment.Moment) => void;
  onAssociatedDocumentTypeChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onElectronicInvoiceChange: (newElectronicInvoice: Invoice | null) => void;
  onAssociatedDocumentStampIdentifierChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAssociatedDocumentEstablishmentCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAssociatedDocumentDispatchPointCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAssociatedDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAssociatedDocumentDateChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onCurrencyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPaymentMethodChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckBankChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTotalAmountChange: (newAmount: number | undefined) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemChange: (index: number, item: ReceiptItem) => void;
  onItemDelete: (index: number) => void;
  onItemsChange: (newItems: ReceiptItem[]) => void;
  onAddItemClick: () => void;
  onPaymentMethodRowChange: (index: number, payment: ReceiptPaymentMethod) => void;
  onPaymentMethodRowDelete: (index: number) => void;
  onAddPaymentMethodClick: () => void;
}

const ReceiptFormContext = createContext<ReceiptFormContextValue | undefined>(
  undefined,
);

export const useReceiptFormContext = () => {
  const context = useContext(ReceiptFormContext);
  if (!context) {
    throw new Error(
      "useReceiptFormContext must be used within ReceiptFormProvider",
    );
  }
  return context;
};

interface ReceiptFormProviderProps {
  children: React.ReactNode;
  taxPayerId?: number;
  readonly?: boolean;
}

export const ReceiptFormProvider: React.FC<ReceiptFormProviderProps> = ({
  children,
  taxPayerId,
  readonly,
}) => {
  const receiptRef = useRef<Receipt | null>(newReceipt(taxPayerId));
  const [receiptDate, setReceiptDate] = useState<Date | undefined>(undefined);
  const [currency, setCurrency] = useState(PYG);
  const [paymentMethod, setPaymentMethod] = useState(
    RECEIPT_PAYMENT_METHOD_CASH,
  );
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null,
  );
  const [dispatchPoint, setDispatchPoint] = useState<DispatchPoint | null>(
    null,
  );
  const [stamp, setStamp] = useState<Stamp | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [associatedDocumentType, setAssociatedDocumentType] = useState<
    string | undefined
  >(undefined);
  const [electronicInvoice, setElectronicInvoice] = useState<Invoice | null>(
    null,
  );
  const [items, setItems] = useState<ReceiptItem[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<ReceiptPaymentMethod[]>([]);
  const [errors, setErrors] = useState<Errors>({});

  const onEstablishmentChange = (newEstablishment: Establishment | null) => {
    if (receiptRef.current === null) return;
    receiptRef.current.establishmentId = newEstablishment?.id;
    receiptRef.current.establishmentCode = newEstablishment?.code;
    setEstablishment(newEstablishment);
  };

  const onDispatchPointChange = (newDispatchPoint: DispatchPoint | null) => {
    if (receiptRef.current === null) return;
    receiptRef.current.dispatchPointId = newDispatchPoint?.id;
    receiptRef.current.dispatchPointIdentifier = newDispatchPoint?.identifier;
    setDispatchPoint(newDispatchPoint);
  };

  const onStampChange = (newStamp: Stamp | null) => {
    if (receiptRef.current === null) return;
    receiptRef.current.stampId = newStamp?.id;
    receiptRef.current.stampIdentifier = newStamp?.identifier;
    receiptRef.current.stampBeginDate = newStamp?.beginDate;
    setStamp(newStamp);
  };

  const onClientChange = (newClient: Client | null) => {
    if (receiptRef.current === null) return;
    receiptRef.current.clientId = newClient?.id;
    receiptRef.current.clientCode = newClient?.code;
    receiptRef.current.clientIsTaxPayer = newClient?.isTaxPayer;
    receiptRef.current.clientRuc = newClient?.ruc;
    receiptRef.current.clientSocialName = newClient?.socialName;
    receiptRef.current.clientFantasyName = newClient?.fantasyName;
    receiptRef.current.clientType = newClient?.type;
    receiptRef.current.clientCountryCode = newClient?.countryCode;
    receiptRef.current.clientHasAddress = newClient?.hasAddress;
    receiptRef.current.clientAddress = newClient?.address;
    receiptRef.current.clientHouseNumber = newClient?.houseNumber;
    receiptRef.current.clientDepartment = newClient?.department;
    receiptRef.current.clientDepartmentDescription =
      newClient?.departmentDescription;
    receiptRef.current.clientDistrict = newClient?.district;
    receiptRef.current.clientDistrictDescription =
      newClient?.districtDescription;
    receiptRef.current.clientCity = newClient?.city;
    receiptRef.current.clientCityDescription = newClient?.cityDescription;
    receiptRef.current.clientPhoneNumber = newClient?.phoneNumber;
    receiptRef.current.clientEmail = newClient?.email;
    setClient(newClient);
  };

  const onReceiptDateChange = (value: string | moment.Moment) => {
    if (receiptRef.current === null) return;
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const receiptDate = new Date((value as moment.Moment).toISOString());
    setReceiptDate(receiptDate);
    receiptRef.current.receiptDate = changeTZToUtc(receiptDate).toISOString();
  };

  const onAssociatedDocumentTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (receiptRef.current === null) return;
    const newAssociatedDocumentType = e.target.value;
    receiptRef.current.associatedDocumentType = newAssociatedDocumentType;

    if (
      newAssociatedDocumentType === ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE
    ) {
      receiptRef.current.associatedDocumentPrintedType = undefined;
      receiptRef.current.associatedDocumentStampIdentifier = undefined;
      receiptRef.current.associatedDocumentEstablishmentCode = undefined;
      receiptRef.current.associatedDocumentDispatchPointCode = undefined;
      receiptRef.current.associatedDocumentNumber = undefined;
      receiptRef.current.associatedDocumentDate = undefined;
    } else {
      receiptRef.current.invoiceCdc = undefined;
      receiptRef.current.invoiceId = undefined;
      setElectronicInvoice(null);
    }

    setAssociatedDocumentType(e.target.value);
  };

  const onElectronicInvoiceChange = (newElectronicInvoice: Invoice | null) => {
    if (receiptRef.current === null) return;
    receiptRef.current.invoiceId = newElectronicInvoice?.id;
    receiptRef.current.invoiceCdc = newElectronicInvoice?.cdc;
    setElectronicInvoice(newElectronicInvoice);
  };

  const onAssociatedDocumentStampIdentifierChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (receiptRef.current === null) return;
    receiptRef.current.associatedDocumentStampIdentifier = e.target.value;
  };

  const onAssociatedDocumentEstablishmentCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (receiptRef.current === null) return;
    receiptRef.current.associatedDocumentEstablishmentCode = e.target.value;
  };

  const onAssociatedDocumentDispatchPointCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (receiptRef.current === null) return;
    receiptRef.current.associatedDocumentDispatchPointCode = e.target.value;
  };

  const onAssociatedDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (receiptRef.current === null) return;
    receiptRef.current.associatedDocumentNumber = e.target.value;
  };

  const onAssociatedDocumentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (receiptRef.current === null) return;
    receiptRef.current.associatedDocumentDate = e.target.value;
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (receiptRef.current === null) return;
    receiptRef.current.currency = e.target.value;
    setCurrency(e.target.value);
  };

  const onPaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (receiptRef.current === null) return;
    receiptRef.current.paymentMethod = e.target.value;
    setPaymentMethod(e.target.value);
  };

  const onCheckNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (receiptRef.current === null) return;
    receiptRef.current.checkNumber = e.target.value;
  };

  const onCheckBankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (receiptRef.current === null) return;
    receiptRef.current.checkBank = e.target.value;
  };

  const onTotalAmountChange = (newAmount: number | undefined) => {
    if (receiptRef.current === null) return;
    receiptRef.current.amount = newAmount;
    setTotalAmount(newAmount);
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (receiptRef.current === null) return;
    receiptRef.current.description = e.target.value;
  };

  const onItemChange = (index: number, item: ReceiptItem) => {
    if (receiptRef.current === null) return;
    const newItems = [...receiptRef.current.items];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemDelete = (index: number) => {
    if (receiptRef.current === null) return;
    const newItems = [...receiptRef.current.items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  const onItemsChange = (newItems: ReceiptItem[]) => {
    if (receiptRef.current === null) return;

    // Calculate total amount from items
    let calculatedAmount = 0;
    for (const item of newItems) {
      if (item.amount !== undefined) {
        calculatedAmount += item.amount;
      }
    }

    receiptRef.current = {
      ...receiptRef.current,
      items: newItems,
      amount: calculatedAmount,
    };

    setItems(newItems);
    setTotalAmount(calculatedAmount);
  };

  const onAddItemClick = () => {
    if (receiptRef.current === null) return;
    const newItems = [...receiptRef.current.items, newReceiptItem()];
    onItemsChange(newItems);
  };

  const onPaymentMethodsChange = (payments: ReceiptPaymentMethod[]) => {
    if (receiptRef.current === null) return;
    receiptRef.current.paymentMethods = payments;
    setPaymentMethods(payments);
  };

  const onPaymentMethodRowChange = (index: number, payment: ReceiptPaymentMethod) => {
    const payments = [...(receiptRef.current?.paymentMethods ?? [])];
    payments[index] = payment;
    onPaymentMethodsChange(payments);
  };

  const onPaymentMethodRowDelete = (index: number) => {
    onPaymentMethodsChange((receiptRef.current?.paymentMethods ?? []).filter((_, ix) => ix !== index));
  };

  const onAddPaymentMethodClick = () => {
    if (receiptRef.current === null) return;
    onPaymentMethodsChange([
      ...(receiptRef.current.paymentMethods ?? []),
      newReceiptPaymentMethod(
        receiptRef.current.currency ?? PYG,
        receiptRef.current.paymentMethod ?? RECEIPT_PAYMENT_METHOD_CASH,
      ),
    ]);
  };

  const value: ReceiptFormContextValue = {
    receiptRef,
    receiptDate,
    currency,
    paymentMethod,
    totalAmount,
    establishment,
    dispatchPoint,
    stamp,
    client,
    associatedDocumentType,
    electronicInvoice,
    items,
    paymentMethods,
    errors,
    readonly,
    setReceiptDate,
    setCurrency,
    setPaymentMethod,
    setTotalAmount,
    setEstablishment,
    setDispatchPoint,
    setStamp,
    setClient,
    setAssociatedDocumentType,
    setElectronicInvoice,
    setItems,
    setPaymentMethods,
    setErrors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onClientChange,
    onReceiptDateChange,
    onAssociatedDocumentTypeChange,
    onElectronicInvoiceChange,
    onAssociatedDocumentStampIdentifierChange,
    onAssociatedDocumentEstablishmentCodeChange,
    onAssociatedDocumentDispatchPointCodeChange,
    onAssociatedDocumentNumberChange,
    onAssociatedDocumentDateChange,
    onCurrencyChange,
    onPaymentMethodChange,
    onCheckNumberChange,
    onCheckBankChange,
    onTotalAmountChange,
    onDescriptionChange,
    onItemChange,
    onItemDelete,
    onItemsChange,
    onAddItemClick,
    onPaymentMethodRowChange,
    onPaymentMethodRowDelete,
    onAddPaymentMethodClick,
  };

  return (
    <ReceiptFormContext.Provider value={value}>
      {children}
    </ReceiptFormContext.Provider>
  );
};
